/**
 * Share-URL encoding for character state.
 *
 * Format: `v<N>.<base64url-encoded-json>`
 *
 * Why base64 JSON instead of bit-packing: ~120 chars is perfectly fine for
 * Discord/blog links, and the schema stays human-debuggable (paste the base64
 * into any decoder and you get readable JSON). No new deps.
 *
 * Why a version prefix: lets `v2` decoders fall back gracefully on old `v1`
 * payloads when new fields are added. Without it, every share URL expires.
 */

const CURRENT_VERSION = 1;

// Short keys on the wire keep the URL compact; we expand back to full store
// field names during decode.
const FIELD_MAP = {
  g: "gender",
  j: "job", // string
  h: "head",
  hp: "headPalette",
  hd: "headdir",
  bp: "bodyPalette",
  a: "action",
  o: "outfit",
  cm: "cashMount",
  it: "topItemId",
  im: "midItemId",
  ib: "botItemId",
  ig: "garmentItemId",
};

// ────────────────────────────────────────────────────────────────────────────
// base64url helpers (no padding, URL-safe alphabet)
// ────────────────────────────────────────────────────────────────────────────

const toBase64Url = (str) => {
  // btoa handles Latin-1 only; we pass JSON so it's always ASCII, safe.
  return btoa(str)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

const fromBase64Url = (str) => {
  // Restore padding for atob
  let b64 = str.replace(/-/g, "+").replace(/_/g, "/");
  const pad = b64.length % 4;
  if (pad) b64 += "=".repeat(4 - pad);
  return atob(b64);
};

// ────────────────────────────────────────────────────────────────────────────
// Encode
// ────────────────────────────────────────────────────────────────────────────

/**
 * Build a share code from the current store state.
 *
 * @param {Object} state - Vuex root state
 * @returns {string} - e.g. "v1.eyJnIjoxLCJqIjoiNDAzNiJ9"
 */
export const encodeShareCode = (state) => {
  const c = state.character;
  const payload = {
    g: c.gender,
    j: c.job[0], // stored as array, encoded as string
    h: c.head,
    hp: c.headPalette,
    hd: c.headdir,
    bp: c.bodyPalette,
    a: c.action,
    o: c.outfit,
    cm: state.cash_mount_checked ? 1 : 0,
    it: state.headgear_top_item?.id || 0,
    im: state.headgear_mid_item?.id || 0,
    ib: state.headgear_bottom_item?.id || 0,
    ig: state.garment_item?.id || 0,
  };
  return `v${CURRENT_VERSION}.${toBase64Url(JSON.stringify(payload))}`;
};

// ────────────────────────────────────────────────────────────────────────────
// Decode
// ────────────────────────────────────────────────────────────────────────────

/**
 * Parse a share code into a normalized payload, or null if invalid/unsupported.
 * Invalid codes are silently rejected (no throw) — the caller falls through to
 * defaults so stale bookmarks don't error-out the whole page.
 *
 * @param {string} code - e.g. "v1.eyJnIjox..."
 * @param {Array}  itemDb - The current item DB, used to hydrate item metadata
 * @returns {Object|null} - { character, topItem, midItem, bottomItem, garmentItem, cashMount }
 */
export const decodeShareCode = (code, itemDb) => {
  if (typeof code !== "string" || !code.includes(".")) return null;
  const [versionTag, body] = code.split(".", 2);
  const match = /^v(\d+)$/.exec(versionTag);
  if (!match) return null;
  const version = parseInt(match[1], 10);
  // For now only v1 is recognized. Future versions should branch here.
  if (version !== 1) return null;

  let data;
  try {
    data = JSON.parse(fromBase64Url(body));
  } catch (_err) {
    return null;
  }
  if (!data || typeof data !== "object") return null;

  // Look up items by DB id and rebuild the viewID + metadata payloads.
  const lookupItem = (id) => {
    if (!id) return null;
    const item = itemDb.find((it) => parseInt(it.id, 10) === parseInt(id, 10));
    return item || null;
  };

  const top = lookupItem(data.it);
  const mid = lookupItem(data.im);
  const bot = lookupItem(data.ib);
  const garment = lookupItem(data.ig);

  // Gender vs job-restriction sanity check. Spec decision: job wins.
  let gender = data.g === 1 ? 1 : 0;
  const jobStr = typeof data.j === "string" ? data.j : String(data.j ?? "0");
  const jobNum = parseInt(jobStr, 10);
  const MALE_ONLY = new Set([19, 4020, 4068, 4211]);
  const FEMALE_ONLY = new Set([20, 4021, 4069, 4212]);
  if (MALE_ONLY.has(jobNum)) gender = 1;
  else if (FEMALE_ONLY.has(jobNum)) gender = 0;

  return {
    character: {
      gender,
      job: [jobStr],
      head: data.h ?? 1,
      headPalette: data.hp ?? 0,
      headdir: data.hd ?? 0,
      headgear: [
        bot ? parseInt(bot.viewID, 10) || 0 : 0,
        top ? parseInt(top.viewID, 10) || 0 : 0,
        mid ? parseInt(mid.viewID, 10) || 0 : 0,
      ],
      garment: garment ? parseInt(garment.viewID, 10) || 0 : 0,
      bodyPalette: data.bp ?? 0,
      action: data.a ?? 0,
      canvas: "200x200+100+150",
      outfit: data.o ?? 0,
    },
    topItem: top || {},
    midItem: mid || {},
    bottomItem: bot || {},
    garmentItem: garment || {},
    cashMount: data.cm ?? 0,
  };
};

// Exposed for tests / debug
export const __internal = { FIELD_MAP, CURRENT_VERSION };
